import { KeenSliderInstance } from 'keen-slider';
import { useCallback, useMemo } from 'react';
import { NavigationCarousel } from '@/features/carousel';
import { CarouselArrowType, CarouselOptions } from '@/features/carousel/types';
import LazyImage from '@/features/image/lazy-image';
import { useMobileDetect } from '@/features/mobile-detect-context/mobile-detect-provider';
import { PUBLIC_IS_BOT } from '@/app/_constants/search-engines';
import { cn } from '@/utils/cn';
import { byDevice } from '../../_utils/carousel-layout';
import { IRANIAN_WOMEN_CAROUSEL } from '../../constants';
import { IranianWomenItemViewModel } from '../../types';
interface Props {
  perView: number;
  activeItem: IranianWomenItemViewModel | null;
  setActiveIndex: (index: number) => void;
  selectedCategory: string | null;
  activeIndex: number;
  handleDetailsChanged: (slider: KeenSliderInstance) => void;
  slides: IranianWomenItemViewModel[];
  totalSlides: number;
  visibleSideCount: number;
  setDetails: (details: IranianWomenItemViewModel | null) => void;
}
const WomenSlider = ({
  perView,
  activeItem,
  setActiveIndex,
  selectedCategory,
  activeIndex,
  handleDetailsChanged,
  slides,
  totalSlides,
  visibleSideCount,
  setDetails
}: Props) => {
  const {
    isMobile
  } = useMobileDetect();
  const effectivePerView = PUBLIC_IS_BOT ? 2 : perView;
  const carouselOptions = useMemo<CarouselOptions>(() => ({
    loop: !PUBLIC_IS_BOT,
    mode: 'snap',
    perView: effectivePerView,
    drag: visibleSideCount > 0,
    slides: {
      origin: PUBLIC_IS_BOT ? 'auto' : 'center',
      perView: effectivePerView,
      spacing: byDevice(IRANIAN_WOMEN_CAROUSEL.slideSpacing, isMobile)
    }
  }), [effectivePerView, isMobile, visibleSideCount]);
  const getDistance = (current: number, target: number, total: number) => {
    const forward = (target - current + total) % total;
    const backward = (current - target + total) % total;
    return Math.min(forward, backward);
  };
  const handleSlideChange = useCallback((index: number) => {
    setActiveIndex(index);
  }, [setActiveIndex]);
  return <NavigationCarousel key={selectedCategory ?? 'all'} className='order-1 lg:order-2' onSlideChange={handleSlideChange} activeIndex={activeIndex} arrowType={isMobile || visibleSideCount === 0 ? undefined : CarouselArrowType.IranianWomen} detailsChanged={handleDetailsChanged} options={carouselOptions} staticChildren data-sentry-element="NavigationCarousel" data-sentry-component="WomenSlider" data-sentry-source-file="women-slider.tsx">
            {slides.map(({
      name,
      image_url
    }, index) => {
      const distance = getDistance(activeIndex, index, totalSlides);
      const isActive = distance === 0;
      const isFirstNeighbour = distance === 1;
      const isSecondNeighbour = distance === 2;
      const isThirdNeighbour = distance === 3;
      const isVisible = distance <= visibleSideCount;
      return <div key={`${name}-${index}`} onClick={() => {
        if (!isActive || !isMobile) return;
        setDetails(activeItem ?? null);
      }} className={cn('keen-slider__slide opacity-0', PUBLIC_IS_BOT ? 'min-w-fit max-w-fit' : 'flex justify-center', PUBLIC_IS_BOT ? 'opacity-1' : [isVisible && 'opacity-1', isActive && 'z-30', isFirstNeighbour && 'z-20', isSecondNeighbour && 'z-10', isThirdNeighbour && 'z-[5]'])}>
                        <div className={cn('relative !w-[184px] shrink-0 origin-center will-change-transform lg:!w-[280px]', PUBLIC_IS_BOT ? 'scale-100' : [isActive && 'scale-100', isFirstNeighbour && 'scale-[76%] lg:scale-[71%]', isSecondNeighbour && 'scale-[54%] lg:scale-[50%]', isThirdNeighbour && 'scale-[40%] lg:scale-[36%]', !isVisible && 'scale-[15%]'])}>
                            <LazyImage src={image_url} alt={name} className={cn('aspect-square !h-[184px] !w-[184px] rounded-lg border border-border-gray-light object-cover object-top lg:!h-[280px] lg:!w-[280px]')} isLazy={!PUBLIC_IS_BOT} />
                        </div>
                    </div>;
    })}
        </NavigationCarousel>;
};
export default WomenSlider;